import { index, modelOptions, mongoose, post, prop, PropType } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { MessagingChannelAddress, channelScopedKey } from "../commonSubSchemas/messagingChannelAddress.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import type { InboundMessageFieldsWithDefaults, InboundMessageSchemaTypes } from "../types/inboundMessage.d.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { InboundMedia } from "./subSchemas/inboundMedia.js";

const SCHEMA_KEY = "inboundMessages" as const;

/**
 * Where a message ended up. Add a queue only once something can route to it. `none` is not a queue, it is
 * "no route matched".
 */
export const INBOUND_MESSAGE_ROUTED_TO = [
    "none",
    "inboundMessageCompliance",
    /**
     * Legacy. Routing produced this for `HELP` before the command worker existed, and nothing consumed it.
     * Kept because stored rows carry it and this collection is an append-only log, so a stored value must
     * still parse. Nothing routes here any more — help is `inboundMessageCommand`.
     */
    "inboundMessageStaticReply",
    "inboundMessageCommand",
] as const;

export type InboundMessageRoutedTo = (typeof INBOUND_MESSAGE_ROUTED_TO)[number];

/**
 * One row per inbound message, on every channel: a log, never a work item, so no processing state lives here.
 * These rows mix organizations, so they must not be read from an org-facing surface. **A row means intake
 * succeeded**: a message that never got through is held on the intake queue and replayed from there.
 */
@index({ "sender.channel": 1, providerMessageId: 1 }, { unique: true })
// One endpoint's messages in send order — the conversation read. No reader exists yet.
@index({ "sender.channel": 1, "sender.address": 1, sentAt: 1 })
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        collection: "inboundMessages" as const satisfies typeof SCHEMA_KEY,
        _id: true,
    },
})
@post<SchemaBase<InboundMessage>>(...addSchemaKeyHook(SCHEMA_KEY))
export class InboundMessage extends SchemaBase<InboundMessage, InboundMessageFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    /**
     * The provider's own id, as sent. Unique only within a provider, so `sender.channel` pairs with it to form the
     * idempotency key — which assumes one provider per channel. A second SMS provider needs a `provider` field
     * here, not a wider index.
     */
    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public providerMessageId!: string;

    @validate({ childClass: MessagingChannelAddress })
    @prop({ type: () => MessagingChannelAddress, required: true })
    public sender!: MessagingChannelAddress;

    /**
     * The exact text received; empty for a media-only payload. Bounded well above any channel's own limit, because
     * it is copied into Redis job data, into Mongo, and verbatim into a log line when an enqueue fails.
     */
    @validate({ zodValidator: z.string().max(8192) })
    @prop({ type: () => String, required: true })
    public body!: string;

    /** Bounded for the same reason as `body`; no provider sends anything close to ten attachments. */
    @validate({
        childClass: InboundMedia,
        childClassTransform: (object) => z.array(object).max(10),
        hasDefault: true,
    })
    @prop({ type: () => [InboundMedia], required: true, default: [] }, PropType.ARRAY)
    public media: InboundMedia[] = [];

    /**
     * When the grower sent it, falling back to `receivedAt` where the provider reports nothing. Named to match
     * `outboundMessages.sentAt` so a conversation sorts on one field across both collections.
     */
    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public sentAt!: Date;

    /** Stamped on receipt rather than reported, so the recorded order of arrival comes from a single clock. */
    @validate({ zodValidator: z.coerce.date() })
    @prop({ type: () => Date, required: true })
    public receivedAt!: Date;

    /**
     * The Descope user this address resolved to, or `null` for "could not authenticate" — no such user, Descope
     * unreachable, or a channel with no identity mapping. `required: false` only because Mongoose's `required`
     * rejects `null`, which is the value that carries the meaning here; absence would be a third state.
     */
    @validate({ zodValidator: z.string().nullable() })
    @prop({ type: () => String, required: false })
    public resolvedDescopeIdentity!: string | null;

    /** Where the message went, spelled as the queue name itself so there is one vocabulary. */
    @validate({ zodValidator: z.enum(INBOUND_MESSAGE_ROUTED_TO) })
    @prop({ type: () => String, required: true, enum: INBOUND_MESSAGE_ROUTED_TO })
    public routedTo!: InboundMessageRoutedTo;

    static getters = {
        /**
         * The message's identity as one string. Declared over the two fields it reads, so the accept path can
         * call it before a row exists. {@link channelScopedKey} is what makes it safe as a BullMQ job id.
         */
        getMessageUid: (self: Pick<InboundMessage, "sender" | "providerMessageId">): string =>
            channelScopedKey(self.sender.channel, self.providerMessageId),
    } as const satisfies Record<string, (self: InboundMessage) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<InboundMessageSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<InboundMessage, InboundMessageFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

InboundMessage.dtoValidators = getZodSchemaFromClass(
    InboundMessage,
) as unknown as DtoValidatorTypes<InboundMessageSchemaTypes>;
